import { useEffect, useState, useCallback, useRef, lazy, Suspense } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import { Filter, Settings, Download, RefreshCw, Search, Columns, FileDown, Save, X } from 'lucide-react';
import { analyticsApi, domainsApi, settingsApi, reportsApi } from '../../services/api';
import { useFilterStore } from '../../store/filterStore';
import DateRangePicker from '../../components/common/DateRangePicker';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FilterPanel from '../../components/common/FilterPanel';
import OfferSelector from '../../components/common/OfferSelector';
import GroupBySelector from '../../components/reports/GroupBySelector';
import AnalyticsTable from '../../components/reports/AnalyticsTable';
import DetailSection from '../../components/reports/DetailSection';
const WorldMap = lazy(() => import('../../components/reports/WorldMap'));
import { REPORT_COLUMNS, GROUP_BY_OPTIONS } from '../../constants/reportOptions';

const LOCALSTORAGE_KEY = 'analytics-report-groupby';

// Default group by fields
const DEFAULT_GROUPBY = [
  GROUP_BY_OPTIONS.find(opt => opt.field === 'channel'),
  GROUP_BY_OPTIONS.find(opt => opt.field === 'subchannel')
].filter(Boolean);

// Helper to get groupBy from URL params only (for shared links)
const getGroupByFromUrl = (searchParams) => {
  const groupByParam = searchParams.get('groupBy');
  if (groupByParam) {
    const fields = groupByParam.split(',');
    const groups = fields
      .map(field => GROUP_BY_OPTIONS.find(opt => opt.field === field))
      .filter(Boolean);
    if (groups.length > 0) return groups;
  }
  return null;
};

// Helper to get groupBy from localStorage (user's session changes)
const getGroupByFromLocalStorage = () => {
  try {
    const stored = localStorage.getItem(LOCALSTORAGE_KEY);
    if (stored) {
      const fields = JSON.parse(stored);
      if (Array.isArray(fields) && fields.length > 0) {
        const groups = fields
          .map(field => GROUP_BY_OPTIONS.find(opt => opt.field === field))
          .filter(Boolean);
        if (groups.length > 0) return groups;
      }
    }
  } catch (e) {
    // Ignore localStorage errors
  }
  return null;
};

// Helper to save groupBy to localStorage
const saveGroupByToLocalStorage = (groups) => {
  try {
    const fields = groups.map(g => g.field);
    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(fields));
  } catch (e) {
    // Ignore localStorage errors
  }
};

// Save Quick Report Modal
function SaveQuickReportModal({ isOpen, onClose, onSave, isSaving }) {
  const [reportName, setReportName] = useState('');

  useEffect(() => {
    if (isOpen) {
      setReportName('');
    }
  }, [isOpen]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (reportName.trim()) {
      onSave(reportName.trim());
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-lg shadow-xl w-full max-w-md">
          <div className="flex items-center justify-between px-6 py-4 border-b border-secondary-200">
            <h3 className="text-lg font-semibold text-secondary-900">Save as Quick Report</h3>
            <button
              onClick={onClose}
              className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-6">
            <div className="mb-4">
              <label htmlFor="reportName" className="block text-sm font-medium text-secondary-700 mb-2">
                Report Name
              </label>
              <input
                id="reportName"
                type="text"
                value={reportName}
                onChange={(e) => setReportName(e.target.value)}
                placeholder="Enter report name..."
                className="w-full px-4 py-2 text-sm border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                autoFocus
              />
            </div>
            <p className="text-sm text-secondary-500 mb-4">
              This will save the current filters, grouping, and options. The saved report will appear in the sidebar under Quick Reports.
            </p>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-secondary-700 bg-secondary-100 hover:bg-secondary-200 rounded-lg"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={!reportName.trim() || isSaving}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    Save Report
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsReport() {
  const [searchParams] = useSearchParams();
  const { startDate, endDate, selectedDkey, setSelectedDomain, getQueryParams } = useFilterStore();
  const initializedRef = useRef(false);
  const queryClient = useQueryClient();

  // UI State
  const [showFilters, setShowFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [activeFilters, setActiveFilters] = useState({ filters: {}, matchType: 'any' });
  // Initialize with default, will be updated when settings load
  const [selectedGroups, setSelectedGroups] = useState(() => {
    // Priority: URL params > localStorage > default (settings applied after load)
    const urlGroups = getGroupByFromUrl(searchParams);
    if (urlGroups) return urlGroups;
    const localGroups = getGroupByFromLocalStorage();
    if (localGroups) return localGroups;
    return DEFAULT_GROUPBY;
  });
  const [sortConfig, setSortConfig] = useState({ key: 'visitors', direction: 'desc' });

  // Fetch user settings to get default group by
  const { data: settingsData } = useQuery({
    queryKey: ['user-settings'],
    queryFn: () => settingsApi.getSettings(),
    staleTime: 5 * 60 * 1000,
  });

  // Check for saved report in URL params
  const savedReportId = searchParams.get('savedReport');

  // Fetch saved report if ID is in URL
  const { data: savedReportData } = useQuery({
    queryKey: ['saved-report', savedReportId],
    queryFn: () => reportsApi.getSavedReports(),
    enabled: !!savedReportId,
    staleTime: 5 * 60 * 1000,
  });

  // Report Options
  const [reportOptions, setReportOptions] = useState({
    usePostDate: false,
    includeBots: false,
  });

  // Table Toolbar State
  const [searchQuery, setSearchQuery] = useState('');
  const [showColumnSelector, setShowColumnSelector] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(
    REPORT_COLUMNS.reduce((acc, col) => ({ ...acc, [col.key]: true }), {})
  );

  // Detail modal state
  const [detailModal, setDetailModal] = useState({
    isOpen: false,
    type: null,
    filters: {},
  });

  // Fetch user's available domains/offers
  const { data: domainsData, isLoading: domainsLoading } = useQuery({
    queryKey: ['domains'],
    queryFn: () => domainsApi.getDomains(),
    staleTime: 5 * 60 * 1000,
  });

  const domains = domainsData?.data?.data || [];
  const defaultOffer = domainsData?.data?.defaultOffer;

  // Apply saved report configuration when loaded
  useEffect(() => {
    if (!savedReportId || !savedReportData?.data?.data) return;

    const savedReports = savedReportData.data.data;
    const report = savedReports.find(r => r.id === parseInt(savedReportId));

    if (report?.config) {
      const config = report.config;

      // Apply groupBy
      if (config.groupBy && Array.isArray(config.groupBy)) {
        const groups = config.groupBy
          .map(field => GROUP_BY_OPTIONS.find(opt => opt.field === field))
          .filter(Boolean);
        if (groups.length > 0) {
          setSelectedGroups(groups);
        }
      }

      // Apply filters
      if (config.filters) {
        setActiveFilters(config.filters);
      }

      // Apply report options
      if (config.reportOptions) {
        setReportOptions(config.reportOptions);
      }

      // Apply domain/offer selection
      if (config.dkey) {
        const domain = domains.find(d => d.dkey === config.dkey);
        if (domain) {
          setSelectedDomain(config.dkey, domain.name);
        }
      }
    }
  }, [savedReportId, savedReportData, domains, setSelectedDomain]);

  // Auto-select default offer from DB, or first domain if no default set
  useEffect(() => {
    if (!selectedDkey && domains.length > 0) {
      if (defaultOffer) {
        setSelectedDomain(defaultOffer.dkey, defaultOffer.name);
      } else {
        setSelectedDomain(domains[0].dkey, domains[0].name);
      }
    }
  }, [domains, defaultOffer, selectedDkey, setSelectedDomain]);

  // Apply user's default group by settings on initial load
  // Only if: no URL params AND no localStorage value AND settings have loaded
  useEffect(() => {
    if (initializedRef.current) return; // Only run once

    const settings = settingsData?.data?.data;
    if (!settings) return; // Wait for settings to load

    // Check if we should apply user's default settings
    const urlGroups = getGroupByFromUrl(searchParams);
    const localGroups = getGroupByFromLocalStorage();

    // If URL or localStorage already set, don't override with settings
    if (urlGroups || localGroups) {
      initializedRef.current = true;
      return;
    }

    // Apply user's default group by from settings
    if (settings.default_groupby && Array.isArray(settings.default_groupby) && settings.default_groupby.length > 0) {
      const groups = settings.default_groupby
        .map(field => GROUP_BY_OPTIONS.find(opt => opt.field === field))
        .filter(Boolean);
      if (groups.length > 0) {
        setSelectedGroups(groups);
      }
    }

    initializedRef.current = true;
  }, [settingsData, searchParams]);

  // Update selectedGroups when URL params change (e.g., from Custom Reports page)
  useEffect(() => {
    const groupByParam = searchParams.get('groupBy');
    if (groupByParam) {
      const newGroups = getGroupByFromUrl(searchParams);
      if (newGroups) {
        setSelectedGroups(newGroups);
        // Also save to localStorage so it persists on refresh
        saveGroupByToLocalStorage(newGroups);
      }
    }
  }, [searchParams]);

  // Handler for when user changes group by - saves to localStorage for persistence
  const handleGroupByChange = useCallback((newGroups) => {
    setSelectedGroups(newGroups);
    saveGroupByToLocalStorage(newGroups);
  }, []);

  // Build query params including filters and grouping
  const getFullQueryParams = useCallback(() => {
    const baseParams = getQueryParams();

    // Add filter params - serialize as JSON to include match types
    Object.entries(activeFilters.filters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        // Serialize filter values with their match types as JSON
        baseParams[key] = JSON.stringify(values);
      }
    });
    baseParams.matchType = activeFilters.matchType;

    // Add group by params
    if (selectedGroups.length > 0) {
      baseParams.groupBy = selectedGroups.map(g => g.field).join(',');
    }

    // Add report options
    baseParams.usePostDate = reportOptions.usePostDate;
    baseParams.includeBots = reportOptions.includeBots;

    return baseParams;
  }, [getQueryParams, activeFilters, selectedGroups, reportOptions]);

  // Fetch report data
  // Serialize all objects in query key to ensure proper change detection
  const groupByKey = selectedGroups.map(g => g.field).join(',');
  const filtersKey = JSON.stringify(activeFilters);
  const sortKey = `${sortConfig.key}-${sortConfig.direction}`;
  const optionsKey = `${reportOptions.usePostDate}-${reportOptions.includeBots}`;
  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['analytics-report', startDate, endDate, selectedDkey, filtersKey, groupByKey, selectedGroups.length, sortKey, optionsKey],
    queryFn: () => analyticsApi.getAnalyticsReport(getFullQueryParams()),
    enabled: !!selectedDkey || domains.length === 0,
    staleTime: 0, // Always consider data stale to ensure refetch on key change
  });

  // Mutation for saving quick reports
  const saveQuickReportMutation = useMutation({
    mutationFn: (name) => reportsApi.saveReport({
      name,
      config: {
        groupBy: selectedGroups.map(g => g.field),
        filters: activeFilters,
        reportOptions,
        dkey: selectedDkey,
      },
      isQuickReport: true,
    }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quick-reports'] });
      setShowSaveModal(false);
    },
  });

  // Check if response is hierarchical (multiple groupBy)
  const isHierarchical = data?.data?.isHierarchical || false;

  // Transform data for table - hierarchical data already has grouping set by backend
  const rawData = (data?.data?.data || []).map((row, idx) => ({
    ...row,
    id: row.id || `row-${idx}`,
    // For hierarchical data, use backend's grouping; for flat data, compute from first group field
    grouping: row.grouping || row[selectedGroups[0]?.field] || row.label || row.date || '-',
  }));

  // Filter data based on search query
  const reportData = searchQuery
    ? rawData.filter(row => {
        const groupValue = String(row.grouping || '').toLowerCase();
        return groupValue.includes(searchQuery.toLowerCase());
      })
    : rawData;

  // Get visible columns
  const filteredColumns = REPORT_COLUMNS.filter(col => visibleColumns[col.key]);

  // CSV Export function
  const handleExportCSV = () => {
    const headers = filteredColumns.map(col => col.label).join(',');
    const rows = reportData.map(row =>
      filteredColumns.map(col => {
        const val = row[col.key];
        if (col.type === 'currency') return `$${Number(val || 0).toFixed(2)}`;
        if (col.type === 'percent') return `${Number(val || 0).toFixed(2)}%`;
        return val || '';
      }).join(',')
    );
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleDomainChange = (dkey, name) => {
    setSelectedDomain(dkey, name);
  };

  const handleFiltersChange = useCallback((newFilters) => {
    setActiveFilters(newFilters);
  }, []);

  const handleCellClick = (columnKey, row) => {
    // Map column key to detail type
    const typeMap = {
      visitors: 'visitors',
      engaged: 'engaged',
      sales: 'sales',
    };

    const type = typeMap[columnKey];
    if (!type) return;

    // Build filters from the row context - include ALL groupBy field values dynamically
    const rowFilters = {};

    // Include all selected groupBy field values from the row
    selectedGroups.forEach(group => {
      const field = group.field;
      if (row[field] !== undefined && row[field] !== null && row[field] !== '') {
        rowFilters[field] = row[field];
      }
    });

    // Include label for drill-down ONLY for landing page related fields
    // Other fields (channel, subchannel, country, etc.) are already sent as direct filters
    const landingPageFields = ['landing_page_variant', 'page', 'variant'];
    const firstGroupField = selectedGroups[0]?.field;
    if (firstGroupField && landingPageFields.includes(firstGroupField) && row[firstGroupField]) {
      rowFilters.label = row[firstGroupField];
    } else if (row.label && !firstGroupField) {
      rowFilters.label = row.label;
    } else if (row.grouping && !firstGroupField) {
      rowFilters.label = row.grouping;
    }

    setDetailModal({
      isOpen: true,
      type,
      filters: rowFilters,
    });
  };

  const handleSort = (newSortConfig) => {
    setSortConfig(newSortConfig);
  };

  // Count active filters
  const activeFilterCount = Object.values(activeFilters.filters).reduce(
    (count, values) => count + (values?.length || 0),
    0
  );

  // Get dynamic first column label
  const tableColumns = filteredColumns.map((col, idx) => {
    if (col.key === 'grouping' && selectedGroups.length > 0) {
      return { ...col, label: selectedGroups[0].label };
    }
    return col;
  });

  // Toggle column visibility
  const toggleColumn = (colKey) => {
    setVisibleColumns(prev => ({
      ...prev,
      [colKey]: !prev[colKey]
    }));
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-secondary-900">Analytics Report</h1>
          <p className="text-secondary-600 mt-1">
            View overall stats with custom groupings and filters
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {/* Offer Selector */}
          {domains.length > 0 && (
            <OfferSelector
              domains={domains}
              selectedDkey={selectedDkey}
              onChange={handleDomainChange}
              disabled={domainsLoading}
            />
          )}

          {/* Refine Query Button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showFilters || activeFilterCount > 0
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-secondary-300 text-secondary-700 hover:border-primary-500'
            }`}
          >
            <Filter className="w-4 h-4" />
            Refine Query
            {activeFilterCount > 0 && (
              <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Settings Button */}
          <button
            onClick={() => setShowSettings(!showSettings)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              showSettings
                ? 'bg-primary-50 border-primary-300 text-primary-700'
                : 'bg-white border-secondary-300 text-secondary-700 hover:border-primary-500'
            }`}
          >
            <Settings className="w-4 h-4" />
            Group By
          </button>

          {/* Refresh Button */}
          <button
            onClick={() => refetch()}
            className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg border border-secondary-300"
            title="Refresh data"
          >
            <RefreshCw className={`w-5 h-5 ${isFetching ? 'animate-spin' : ''}`} />
          </button>

          <DateRangePicker />
        </div>
      </div>

      {/* Settings Panel - Group By */}
      {showSettings && (
        <GroupBySelector
          selectedGroups={selectedGroups}
          onChange={handleGroupByChange}
        />
      )}

      {/* Filter Panel */}
      {showFilters && <FilterPanel onFiltersChange={handleFiltersChange} />}

      {/* Report Options Panel */}
      <div className="bg-white rounded-lg border border-secondary-200 p-4">
        <div className="flex flex-wrap items-center gap-6">
          <div className="text-sm font-medium text-secondary-700">Report Options:</div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={reportOptions.usePostDate}
              onChange={(e) => setReportOptions(prev => ({ ...prev, usePostDate: e.target.checked }))}
              className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-secondary-600">Use Post Date instead of Event Date</span>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={reportOptions.includeBots}
              onChange={(e) => setReportOptions(prev => ({ ...prev, includeBots: e.target.checked }))}
              className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
            />
            <span className="text-sm text-secondary-600">Include Bots (Google bots, Bing bots, ...)</span>
          </label>
        </div>
      </div>

      {/* Table Toolbar */}
      <div className="bg-white rounded-lg border border-secondary-200 p-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search in table..."
              className="pl-9 pr-4 py-2 text-sm border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent w-64"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          {/* Column Visibility */}
          <div className="relative">
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary-600 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50"
            >
              <Columns className="w-4 h-4" />
              Columns
            </button>
            {showColumnSelector && (
              <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-secondary-200 rounded-lg shadow-lg z-20 p-2">
                {REPORT_COLUMNS.map(col => (
                  <label
                    key={col.key}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-secondary-50 rounded cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={visibleColumns[col.key]}
                      onChange={() => toggleColumn(col.key)}
                      className="w-4 h-4 text-primary-600 border-secondary-300 rounded focus:ring-primary-500"
                    />
                    <span className="text-sm text-secondary-700">{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Download CSV */}
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary-600 bg-white border border-secondary-300 rounded-lg hover:bg-secondary-50"
          >
            <FileDown className="w-4 h-4" />
            Download
          </button>

          {/* Save as Quick Report */}
          <button
            onClick={() => setShowSaveModal(true)}
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg"
          >
            <Save className="w-4 h-4" />
            Save Report
          </button>
        </div>
      </div>

      {/* World Map - Show when grouping by country */}
      {selectedGroups.length > 0 && selectedGroups[0].field === 'country' && !isLoading && reportData.length > 0 && (
        <Suspense fallback={null}>
          <WorldMap data={reportData} />
        </Suspense>
      )}

      {/* Report Table */}
      {error ? (
        <div className="card p-8 text-center">
          <p className="text-red-600">Error loading report data</p>
          <button
            onClick={() => refetch()}
            className="mt-4 px-4 py-2 text-sm font-medium text-primary-600 hover:text-primary-700"
          >
            Try Again
          </button>
        </div>
      ) : (
        <AnalyticsTable
          data={reportData}
          columns={tableColumns}
          groupByFields={selectedGroups}
          onCellClick={handleCellClick}
          onSort={handleSort}
          sortConfig={sortConfig}
          isLoading={isLoading}
          title={`Analytics Report${selectedGroups.length > 0 ? ` by ${selectedGroups.map(g => g.label).join(' > ')}` : ''}`}
        />
      )}

      {/* Detail Section - Inline below table */}
      <DetailSection
        isOpen={detailModal.isOpen}
        onClose={() => setDetailModal({ isOpen: false, type: null, filters: {} })}
        type={detailModal.type}
        filters={detailModal.filters}
        queryParams={getFullQueryParams()}
      />

      {/* Save Quick Report Modal */}
      <SaveQuickReportModal
        isOpen={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        onSave={(name) => saveQuickReportMutation.mutate(name)}
        isSaving={saveQuickReportMutation.isPending}
      />
    </div>
  );
}
